import React from 'react';

import classNames from 'classnames';
import { toast as rawToast, ToastContainer as RawToastContainer } from 'react-toastify';

import { AlertMessageBody } from '../DesignSystem/components/AlertMessage/AlertMessageBody';
import { CloseButton } from '../DesignSystem/components/AlertMessage/CloseButton';
import { toastContainerContextClass } from '../DesignSystem/components/AlertMessage/constants';
import { layoutGrid } from '../DesignSystem/components/Layout/utils';

import type { Level } from '../DesignSystem/components/AlertMessage/types';
import type { Id, ToastOptions } from 'react-toastify';

const defaultToastOptions: ToastOptions = {
  icon: false,
  theme: 'colored',
};

type Props = {
  message?: React.ReactNode;
  title: React.ReactNode;
  level: Level;
  actionLabel?: string;
  href?: string;
  to?: string;
  onClick?: () => void;
};

const Notif: React.FC<Props> = ({ level, message, title, href, to, actionLabel, onClick }) => (
  <div data-testid={`alert-${level}`} className="flex grow">
    <AlertMessageBody
      title={title}
      level={level}
      to={to}
      href={href}
      actionLabel={actionLabel}
      onClick={onClick}
    >
      {message}
    </AlertMessageBody>
  </div>
);

type ToastMethod = (
  title: React.ReactNode,
  options?: Pick<Props, 'message' | 'to' | 'href' | 'actionLabel' | 'onClick'> & {
    toastOptions?: ToastOptions;
  },
) => Id;

type CloseButtonProps = {
  closeToast: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type: any;
};

const CloseButtonWithTitle: React.FC<CloseButtonProps> = ({ closeToast, type }) => (
  <CloseButton level={type} onClickClose={closeToast} />
);

export const toast: { [key in Level]: ToastMethod } & { dismiss: (toastId: number) => void } = {
  success: (title, options) =>
    rawToast.success(<Notif title={title} level="success" {...options} />, {
      ...defaultToastOptions,
      ...options?.toastOptions,
      closeButton: props => <CloseButtonWithTitle {...props} />,
    }),
  error: (title, options) =>
    rawToast.error(<Notif title={title} level="error" {...options} />, {
      ...defaultToastOptions,
      ...options?.toastOptions,
      closeButton: props => <CloseButtonWithTitle {...props} />,
    }),
  warning: (title, options) =>
    rawToast.warning(<Notif title={title} level="warning" {...options} />, {
      ...defaultToastOptions,
      ...options?.toastOptions,
      closeButton: props => <CloseButtonWithTitle {...props} />,
    }),
  info: (title, options) =>
    rawToast.info(<Notif title={title} level="info" {...options} />, {
      ...defaultToastOptions,
      ...options?.toastOptions,
      closeButton: props => <CloseButtonWithTitle {...props} />,
    }),
  dismiss: (toastId: number) => rawToast.dismiss(toastId),
};

export const ToastContainer: React.FC = () => (
  <RawToastContainer
    className={classNames(
      'pointer-events-none! w-full! grid!',
      'top-md! left-0! transform-none!',
      'px-md sm-desktop:px-lg md-desktop:px-xl lg-desktop:px-[160px]',
      layoutGrid,
    )}
    toastClassName={params =>
      classNames(
        'pointer-events-auto sm-desktop:col-start-5 col-span-full sm-desktop:col-span-4 outline rounded shadow-xs p-xs pl-sm min-h-0 flex items-start cursor-auto mb-sm',
        toastContainerContextClass[params?.type || 'default'],
      )
    }
    position="top-center"
    autoClose={3000}
    closeOnClick={false}
    hideProgressBar
  />
);
